import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, X, Loader2, Info } from "lucide-react";
import { acceptDeal } from "../../requests/Uploaded_Deals/UploadedDealsService";
import { Formik, Form } from "formik";
import ErrorModal from "./ErrorModal";
import { useState } from "react";

const AcceptDealModal = ({
  isOpen,
  onClose,
  deal,
  dealId,
  onSuccess,
  onError,
  type = "accept",
}) => {
  const [errorModalState, setErrorModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const handleErrorModalClose = () => {
    setErrorModalState({ ...errorModalState, isOpen: false });
    onClose();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await acceptDeal(values, dealId, {
        ...actions,
        onSuccess: () => {
          onSuccess();
          onClose();
        },
        onError: (error) => {
          if (error?.response?.data?.status_code === "998") {
            setErrorModalState({
              isOpen: true,
              title: "Sender ID Error",
              message:
                error?.response?.data?.message === "Vendor has no sender id or rejected"
                  ? "The sender ID was not found in the system/it is declined. Please verify the sender ID and try again."
                  : "The sender ID has not been approved. Please ensure the sender ID is approved before proceeding.",
            });
          } else {
            onError && onError(error);
          }
        },
      });
    } catch (err) {
      console.error("Error in handleSubmit:", err);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999] bg-black/50 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
              className="fixed inset-0 z-[1000] flex w-full items-center justify-center p-4 pointer-events-none"
            >
              <div
                className="pointer-events-auto w-full max-w-md min-w-0 rounded-2xl bg-white shadow-[0_25px_60px_-12px_rgba(0,0,0,0.28)] overflow-hidden box-border"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="accept-modal-title"
              >
                {/* Top accent stripe */}
                <div className="h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500" />

                <Formik
                  initialValues={{ status: "Approved" }}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      {/* Header */}
                      <div className="px-6 pt-5 pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3.5 min-w-0">
                            <div className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 overflow-hidden">
                              <h2
                                id="accept-modal-title"
                                className="text-lg font-bold text-gray-900 leading-tight break-words"
                              >
                                Accept Deal
                              </h2>
                              <p className="text-xs text-gray-500 mt-0.5 break-words">
                                Approve and publish this deal
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="p-1.5 shrink-0 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            aria-label="Close"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Deal name card */}
                      <div className="px-6 pb-4 min-w-0 overflow-hidden">
                        <div className="flex items-start gap-3 px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-200/80 min-w-0 overflow-hidden">
                          <div className="w-1 self-stretch shrink-0 rounded-full bg-emerald-400" />
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
                              Deal
                            </p>
                            <p className="text-sm font-semibold text-gray-900 break-words whitespace-normal leading-snug w-full">
                              {deal?.title ?? `#${deal?.id ?? "—"}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Info notice */}
                      <div className="px-6 pb-5 min-w-0 overflow-hidden">
                        <div className="flex items-start gap-2.5 px-3.5 py-3 bg-emerald-50 rounded-xl border border-emerald-100 min-w-0 overflow-hidden">
                          <Info className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <p className="text-xs text-emerald-700 leading-relaxed break-words whitespace-normal min-w-0 w-full">
                            Accepting this deal will make it{" "}
                            <strong>live and visible</strong> to consumers on
                            the platform immediately.
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="px-6 pb-6 flex gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          disabled={isSubmitting}
                          className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-60"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm shadow-emerald-200 disabled:opacity-70"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Accepting…
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              Accept Deal
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ErrorModal
        isOpen={errorModalState.isOpen}
        onClose={handleErrorModalClose}
        errorTitle={errorModalState.title}
        errorMessage={errorModalState.message}
      />
    </>
  );
};

export default AcceptDealModal;
